import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Info } from "lucide-react";
import { TYPES, TYPE_LABEL, fmtDays } from "@/components/leave/shared";

const EMPTY = { leave_type: "CL", duration: "full", from_date: "", to_date: "", reason: "", contact_number: "" };

const ReadOnly = ({ label, value, testid }) => (
  <div>
    <Label className="label-eyebrow">{label}</Label>
    <div className="mt-1 h-10 px-3 flex items-center bg-[#F9FAFB] border border-[#E5E7EB] text-sm" data-testid={testid}>{value || "—"}</div>
  </div>
);

export const ApplyLeaveDialog = ({ open, onClose, summary, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [ev, setEv] = useState(null);
  const [busy, setBusy] = useState(false);
  const emp = summary?.employee || {};
  const bal = summary?.balances?.[f.leave_type];

  useEffect(() => { if (open) { setF({ ...EMPTY, contact_number: emp.phone || "" }); setFile(null); setEv(null); } }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!f.from_date || !(f.to_date || f.duration === "half")) { setEv(null); return; }
    const body = { ...f, to_date: f.duration === "half" ? f.from_date : f.to_date };
    const t = setTimeout(() => api.post("/leave/preview", body).then((r) => setEv(r.data)).catch((e) => setEv({ errors: [e?.response?.data?.detail || "Invalid dates"], warnings: [] })), 250);
    return () => clearTimeout(t);
  }, [f.leave_type, f.duration, f.from_date, f.to_date]); // eslint-disable-line

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const docRequired = !!summary?.doc_mandatory?.[f.leave_type];

  const submit = async () => {
    if (!f.reason.trim()) { toast.error("Reason is required"); return; }
    if (docRequired && !file) { toast.error("Please attach a supporting document"); return; }
    setBusy(true);
    try {
      let att = {};
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/leave/attachments", fd);
        att = { attachment_path: up.data.path, attachment_name: up.data.name };
      }
      await api.post("/leave/applications", { ...f, to_date: f.duration === "half" ? f.from_date : f.to_date, ...att });
      toast.success("Leave application submitted");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="apply-leave-dialog">
        <DialogHeader><DialogTitle className="font-black tracking-tight">Apply for Leave</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ReadOnly label="Employee" value={emp.name} testid="apply-emp-name" />
          <ReadOnly label="Employee ID" value={emp.employee_code} testid="apply-emp-code" />
          <ReadOnly label="Department" value={emp.department} testid="apply-emp-dept" />
          <ReadOnly label="Designation" value={emp.designation} testid="apply-emp-desig" />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="label-eyebrow">Leave Type</Label>
            <Select value={f.leave_type} onValueChange={set("leave_type")}>
              <SelectTrigger className="rounded-none mt-1 h-10" data-testid="apply-leave-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]} ({fmtDays(summary?.balances?.[t]?.available)} available)</SelectItem>)}
              </SelectContent>
            </Select>
            {bal && <div className="text-[11px] text-[#4B5563] mt-1" data-testid="apply-available">Available: <b>{fmtDays(bal.available)}</b>{bal.pending ? ` · ${fmtDays(bal.pending)} pending` : ""}</div>}
          </div>
          <div>
            <Label className="label-eyebrow">Duration</Label>
            <Select value={f.duration} onValueChange={set("duration")}>
              <SelectTrigger className="rounded-none mt-1 h-10" data-testid="apply-duration"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="full">Full Day</SelectItem><SelectItem value="half">Half Day</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">From Date</Label>
            <Input type="date" value={f.from_date} onChange={set("from_date")} className="rounded-none mt-1 h-10" data-testid="apply-from" />
          </div>
          <div>
            <Label className="label-eyebrow">To Date</Label>
            <Input type="date" value={f.duration === "half" ? f.from_date : f.to_date} disabled={f.duration === "half"} min={f.from_date}
              onChange={set("to_date")} className="rounded-none mt-1 h-10" data-testid="apply-to" />
          </div>
        </div>
        <div className="flat-card p-3 mt-2 flex items-center justify-between" data-testid="apply-days-box">
          <span className="label-eyebrow">Number of leave days</span>
          <span className="font-black text-2xl" data-testid="apply-days">{ev?.days != null ? fmtDays(ev.days) : "—"}</span>
        </div>
        {ev?.errors?.map((m) => <div key={m} className="text-xs text-[#B91C1C] flex gap-1.5 items-start" data-testid="apply-error"><AlertTriangle className="w-3.5 h-3.5 mt-0.5" />{m}</div>)}
        {ev?.warnings?.map((m) => <div key={m} className="text-xs text-[#B45309] flex gap-1.5 items-start" data-testid="apply-warning"><Info className="w-3.5 h-3.5 mt-0.5" />{m}</div>)}
        <div className="text-[10px] text-[#9CA3AF]">Weekly offs and declared holidays are not counted. Balance is deducted only after approval.</div>
        <div>
          <Label className="label-eyebrow">Reason for Leave</Label>
          <Textarea rows={3} value={f.reason} onChange={set("reason")} className="rounded-none mt-1" data-testid="apply-reason" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Contact Number During Leave</Label>
            <Input value={f.contact_number} onChange={set("contact_number")} className="rounded-none mt-1 h-10" data-testid="apply-contact" />
          </div>
          <div>
            <Label className="label-eyebrow">Attachment {docRequired ? "*" : "(optional)"}</Label>
            <Input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-none mt-1 h-10" data-testid="apply-attachment" />
          </div>
        </div>
        <Button onClick={submit} disabled={busy || !ev || ev.errors?.length > 0} data-testid="apply-submit"
          className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 mt-2">
          {busy ? "Submitting…" : "Submit Application"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
